import {AfterViewInit, Component, DestroyRef, OnDestroy, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {filter} from 'rxjs';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {EventsManager} from 'src/app/services/events.manager';
import {PlansService} from 'src/app/services/plans.service';
import {BenefitPlanTemplateInterface, MainPageDetailInterface} from 'src/app/interfaces/plans.interface';
import {StateSelectionComponent} from 'src/app/dialogs/state-selection/state-selection.component';
import {UtilService} from 'src/app/services/util.service';
import {StateInterface} from 'src/app/interfaces/state.interface';
import {StateRequestInterface} from 'src/app/interfaces/state-request.interface';
import {InfoModel} from 'src/app/models/info.model';
import {MarketingDetailsRequestInterface} from 'src/app/interfaces/marketing-details-request.interface';
import {LoadingComponent} from 'src/app/components/loading/loading.component';
import {FooterComponent} from 'src/app/components/footer/footer.component';
import {SubheaderComponent} from 'src/app/components/subheader/subheader.component';
import {DOCUMENT, NgIf} from '@angular/common';
import {HeaderComponent} from 'src/app/components/header/header.component';
import {PURCHASE_BASE_PATH, SGDP_ORGANIZATION_ID, WIDGET_STYLES_BUNDLE_ID} from 'src/app/constants/purchase.constant';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [HeaderComponent, NgIf, SubheaderComponent, RouterOutlet, FooterComponent, LoadingComponent]
})

export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private _eventsManager = inject(EventsManager);
  private _plansService = inject(PlansService);
  private _utilService = inject(UtilService);
  private _destroyRef = inject(DestroyRef);
  private _router: Router = inject(Router);
  private _document: Document = inject(DOCUMENT);

  public ngbModal = inject(NgbModal);

  public office?: MainPageDetailInterface = undefined;
  public showSubHeader: boolean = true;

  public ngOnInit() {
    const urlPath: string = window.location.pathname;

    window.scrollTo(0, 0);

    this._eventsManager.orgId = SGDP_ORGANIZATION_ID;
    const planName = 'Smile Generation Dental Plan';

    this._plansService.getSellableStatesByPlan(planName).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(
      (states: any) => {
        this._eventsManager.allowedStates = this._utilService.setAllowedStates(states);
      }
    );
    if (!urlPath.includes('adha')) {
      if (!sessionStorage['state']) {
        this.openStateModal();
      } else {
        this.getPlansByState();
      }
    }

    this._eventsManager.showHideSubheader.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((data: boolean) =>{
      this.showSubHeader = data;
    });

    this._utilService.getStates().pipe(takeUntilDestroyed(this._destroyRef)).subscribe(
      (states: StateInterface[]) => {
        this._eventsManager.setStates(states);
      }
    );

    this._plansService.getContactDetails('sgdp').pipe(takeUntilDestroyed(this._destroyRef)).subscribe(
      (result: InfoModel) => {
        this._eventsManager.contactInfo = result;
        this._eventsManager.contactInfoUpdated.emit();
      }
    );

    this._router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), takeUntilDestroyed(this._destroyRef))
      .subscribe((event: NavigationEnd) => this._toggleWidgetStyles(event.urlAfterRedirects));

    this._toggleWidgetStyles(this._router.url);
  }

  public ngAfterViewInit() {
    window.scroll(0, 0);
  }

  public ngOnDestroy() {
    sessionStorage['state'] = null;
  }

  public openStateModal(): void {
    const modalRef: NgbModalRef = this.ngbModal.open(StateSelectionComponent);
    modalRef.result.then(
      () => {
        this.getPlansByState();
        this.closeModal();
      }
    );
  }

  public closeModal() {
    this.ngbModal.dismissAll();
    if (window.scrollY > 0) {
      window.scrollTo(0, 0);
    }
  }

  public getPlansByState() {
    this._eventsManager.selectedState = JSON.parse(sessionStorage['state']);
    const planPrefix: string = 'Smile Generation Dental Plan';

    const req: StateRequestInterface = {Organization: this._eventsManager.orgId, PlanPrefix:planPrefix, State:this._eventsManager.selectedState.abbr};
    this._plansService.getPlanDetails(req).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(
      (result: BenefitPlanTemplateInterface) => {
        this._eventsManager.planOptions = result;
        this._eventsManager.planYear = new Date().getFullYear().toString();
        this._eventsManager.setIsAdminBy(this._eventsManager.planOptions.templates[0].isAdminBy);
        this._eventsManager.planId = this._eventsManager.planOptions.templates[0].benefitPlanTemplateId;
        this._eventsManager.planName = this._eventsManager.planOptions.templates[0].planName;
        this._eventsManager.planDataUpdated.emit(this._eventsManager.planOptions.templates[0].planName);
        this.getHomePageDetails();
      }
    );
  }

  public getHomePageDetails() {
    const request: MarketingDetailsRequestInterface = {pageName: 'home', planId: this._eventsManager.planId};
    this._plansService.getHomePageData(request).pipe(takeUntilDestroyed(this._destroyRef)).subscribe(
      (result: any) => {
        this._eventsManager.homePageDetails = result;
        this._eventsManager.homePageDetailUpdated.emit();
      }
    );
  }

  private _toggleWidgetStyles(url: string): void {
    if (url.startsWith(PURCHASE_BASE_PATH)) {
      this._addWidgetStyles();
    } else {
      this._document.getElementById(WIDGET_STYLES_BUNDLE_ID)?.remove();
    }
  }

  private _addWidgetStyles(): void {
    if (this._document.getElementById(WIDGET_STYLES_BUNDLE_ID)) {
      return;
    }
    const link: HTMLLinkElement = this._document.createElement('link');
    link.id = WIDGET_STYLES_BUNDLE_ID;
    link.rel = 'stylesheet';
    link.href = `/${ WIDGET_STYLES_BUNDLE_ID }.css`;
    this._document.head.appendChild(link);
  }
}
